import { Injectable } from '@angular/core';
import { SharedService } from './shared.service';
import { MatSnackBar } from '@angular/material/snack-bar';

@Injectable({
  providedIn: 'root'
})
export class AgentStatusTransitionService {

  constructor(
    private sharedService: SharedService,
    private snackBar: MatSnackBar
  ) { }

  /**
   * Prüft, ob ein Statusübergang für einen Agenten erlaubt ist
   * 
   * @param fromStatus - Aktueller Status des Agenten
   * @param toStatus - Zielstatus für den Agenten
   * @param currentRole - Aktuelle Benutzerrolle (Admin, Client, Partner)
   * @param agent - Das Agent-Objekt mit zusätzlichen Informationen
   * @param showWarning - Ob Warnmeldungen angezeigt werden sollen
   * @returns {boolean} - True, wenn der Übergang erlaubt ist, sonst False
   */
  isStatusTransitionAllowed(
    fromStatus: string,
    toStatus: string,
    currentRole: string,
    agent: any,
    showWarning: boolean = true
  ): boolean {
    // ---------------------------------------------------------------------
    // GOD MODE: Admins can always move agents between any statuses.
    // ---------------------------------------------------------------------
    if (currentRole === 'Admin') {
      return true
    }

    let allowMove = false
    let warningMessage = ''
    let allowedRoles: string[] = []
    let allowedStatuses: string[] = []

    switch (fromStatus) {
      case 'AgentCreatedFilesMissing': // Neuer Agent
        allowedStatuses = ['ApplicantGetsChecked']
        if (toStatus === 'ApplicantGetsChecked' && currentRole === 'Admin') allowMove = true
        break

      case 'ApplicantGetsChecked': // In Prüfung
        if ((toStatus === 'WaitingForAgent' || toStatus === 'ApplicantRejected') && currentRole === 'Admin') allowMove = true
        else if (toStatus === 'AgentCreatedFilesMissing' && currentRole === 'Admin') allowMove = true
        break

      case 'WaitingForAgent': // Warte auf Agent / voicefile
        allowedStatuses = ['ApplicantGetsChecked', 'ApplicantRejected', 'AppointmentScheduled', 'Assessment', 'ApplicantAccepted', 'ApplicantConfirmed']
        allowMove = this.checkIfAllowed(toStatus, ['Admin'], allowedStatuses, currentRole)

        // Spezialfall "Dem Kunden vorgeschlagen"
        const hasValidVoicefile = agent.voiceFiles?.some((obj: any) => obj.status === 'accepted') || 
                                agent.videoFiles?.some((obj: any) => obj.status === 'accepted')
        allowMove = allowMove || (this.checkIfAllowed(toStatus, ['Admin'], ['ApplicantGetsSuggested'], currentRole) && hasValidVoicefile)
        break
        
      case 'ApplicantGetsSuggested': // Dem Kunden vorgeschlagen
        allowMove = this.checkIfAllowed(toStatus, ['Admin'], ['ApplicantConfirmed'], currentRole)

        allowedRoles = ['Admin', 'Client']
        allowedStatuses = ['WaitingForAgent', 'ApplicantRejected', 'AppointmentScheduled', 'Assessment', 'ApplicantAccepted']
        allowMove = allowMove || this.checkIfAllowed(toStatus, allowedRoles, allowedStatuses, currentRole)
        break
        
      case 'ApplicantRejected': // Negative Auswahl
        allowMove = this.checkIfAllowed(toStatus, ['Admin'], [agent.previousStatus], currentRole)

        allowedStatuses = ['AppointmentScheduled', 'Assessment', 'ApplicantAccepted']
        allowMove = allowMove || this.checkIfAllowed(toStatus, ['Client'], allowedStatuses, currentRole)
        break

      case 'AppointmentScheduled': // Termin gewünscht / vereinbart
        allowMove = this.checkIfAllowed(toStatus, ['Admin'], ['ApplicantGetsSuggested', 'ApplicantConfirmed'], currentRole)

        allowedStatuses = ['ApplicantRejected', 'Assessment', 'ApplicantAccepted']
        allowMove = allowMove || this.checkIfAllowed(toStatus, ['Admin', 'Client'], allowedStatuses, currentRole)
        break

      case 'Assessment': // Assessment
        allowMove = this.checkIfAllowed(toStatus, ['Admin'], ['ApplicantConfirmed'], currentRole)
        
        allowedStatuses = ['ApplicantRejected', 'AppointmentScheduled', 'ApplicantAccepted']
        allowMove = allowMove || this.checkIfAllowed(toStatus, ['Admin', 'Client'], allowedStatuses, currentRole)
        break
        
      case 'ApplicantAccepted': // Von Zusage Auftraggeber, ehemals "Positive Auswahl"
        allowMove = this.checkIfAllowed(toStatus, ['Admin'], ['ApplicantConfirmed'], currentRole)

        allowedStatuses = ['ApplicantRejected', 'AppointmentScheduled', 'Assessment']
        allowMove = allowMove || this.checkIfAllowed(toStatus, ['Admin', 'Client'], allowedStatuses, currentRole)
        break

      case 'ApplicantConfirmed': // Von Zusage Agent
        allowMove = this.checkIfAllowed(toStatus, ['Admin'], ['ApplicantRejected', 'ApplicantAccepted'], currentRole)
        break

      default:
        break
    }

    if (showWarning && !allowMove) {
      const standardMessage = `Als ${currentRole} können Sie nicht von ${this.sharedService.statuses[fromStatus]?.display || fromStatus} zu ${this.sharedService.statuses[toStatus]?.display || toStatus} verschieben.`
      this.showToast(warningMessage || standardMessage)
    }

    return allowMove
  }

  /**
   * Prüft, ob für die gegebene Rolle und den Zielstatus eine Statusänderung erlaubt ist
   */
  private checkIfAllowed(targetStatus: string, allowedRoles: string[], allowedStatuses: string[], currentRole: string): boolean {
    const hasRightRole = allowedRoles.includes(currentRole)
    if (!hasRightRole) return false

    const isStatusChangeAllowed = allowedStatuses.includes(targetStatus)
    return isStatusChangeAllowed
  }

  /**
   * Holt alle erlaubten Zielstatus für einen Agenten
   * 
   * @param currentStatus - Aktueller Status des Agenten
   * @param currentRole - Aktuelle Benutzerrolle (Admin, Client, Partner)
   * @param agent - Das Agent-Objekt mit zusätzlichen Informationen
   * @returns {string[]} - Liste der erlaubten Zielstatus
   */
  getAllowedTargetStatuses(currentStatus: string, currentRole: string, agent: any): string[] {
    const allowedStatuses: string[] = []
    
    // Immer den aktuellen Status hinzufügen
    allowedStatuses.push(currentStatus)
    
    // Alle möglichen Zielstatuses durchgehen und prüfen
    const allPossibleStatuses = Object.keys(this.sharedService.statuses)
    
    allPossibleStatuses.forEach(targetStatus => {
      // Ignoriere den aktuellen Status
      if (targetStatus !== currentStatus) {
        if (this.isStatusTransitionAllowed(currentStatus, targetStatus, currentRole, agent, false)) {
          allowedStatuses.push(targetStatus)
        }
      }
    })
    
    return allowedStatuses
  }

  private showToast(message: string) {
    this.snackBar.open(message, '', {
      duration: 5000,
      horizontalPosition: 'end', 
      verticalPosition: 'top',
    })
  }
}
